import React from 'react';
import { Facebook, Instagram, Twitter, Link, Truck } from 'lucide-react';
import { Restaurant, SocialLinks, DeliveryServices } from '@/types/menu';
import { LanguageCode, MENU_UI_TRANSLATIONS } from '@/constants/menuUITranslations';

interface SimpleMenuFooterProps {
  restaurant: Restaurant;
  currentLanguage?: LanguageCode;
}

const SimpleMenuFooter: React.FC<SimpleMenuFooterProps> = ({ restaurant, currentLanguage = 'fr' }) => {
  const t = MENU_UI_TRANSLATIONS[currentLanguage];

  // Parse the JSON fields safely
  const getSocialLinks = (): SocialLinks => {
    try {
      if (typeof restaurant.social_links === 'string') {
        return JSON.parse(restaurant.social_links);
      } else if (typeof restaurant.social_links === 'object' && restaurant.social_links !== null) {
        return restaurant.social_links as SocialLinks;
      }
    } catch (e) {
      console.error('Error parsing social links:', e);
    }
    return {};
  };
  
  const getDeliveryServices = (): DeliveryServices => {
    try {
      if (typeof restaurant.delivery_services === 'string') {
        return JSON.parse(restaurant.delivery_services);
      } else if (typeof restaurant.delivery_services === 'object' && restaurant.delivery_services !== null) {
        return restaurant.delivery_services as DeliveryServices;
      }
    } catch (e) {
      console.error('Error parsing delivery services:', e);
    }
    return {};
  };

  // Helper function to ensure URLs are properly formatted
  const formatUrl = (url: string): string => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    if (url.match(/^[\d\s\-\+\(\)]+$/)) {
      return `tel:${url}`;
    }
    return `https://${url}`;
  };
  
  const socialLinks = getSocialLinks();
  const deliveryServices = getDeliveryServices();
  
  const hasSocialLinks = socialLinks && (socialLinks.facebook || socialLinks.instagram || socialLinks.twitter);
  const hasDeliveryServices = deliveryServices && (deliveryServices.deliveroo || deliveryServices.ubereats || deliveryServices.custom);

  return (
    <footer className="mt-8 border-t border-border py-6 px-4">
      {hasSocialLinks && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold mb-2">{t.followUs}</h3>
          <div className="flex gap-4">
            {socialLinks?.facebook && (
              <a 
                href={formatUrl(socialLinks.facebook)} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground"
              >
                <Facebook className="h-5 w-5" />
                <span className="sr-only">Facebook</span>
              </a>
            )}
            {socialLinks?.instagram && (
              <a 
                href={formatUrl(socialLinks.instagram)} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground"
              >
                <Instagram className="h-5 w-5" />
                <span className="sr-only">Instagram</span>
              </a>
            )}
            {socialLinks?.twitter && (
              <a 
                href={formatUrl(socialLinks.twitter)} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground"
              >
                <Twitter className="h-5 w-5" />
                <span className="sr-only">Twitter</span>
              </a>
            )}
          </div>
        </div>
      )}

      {hasDeliveryServices && (
        <div>
          <h3 className="text-sm font-semibold mb-2">{t.order}</h3>
          <div className="flex flex-wrap gap-4">
            {deliveryServices?.deliveroo && (
              <a 
                href={formatUrl(deliveryServices.deliveroo)}
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-muted rounded-md text-sm hover:bg-muted/80"
              >
                <Link className="h-4 w-4" />
                Deliveroo
              </a>
            )}
            {deliveryServices?.ubereats && (
              <a 
                href={formatUrl(deliveryServices.ubereats)}
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-muted rounded-md text-sm hover:bg-muted/80"
              >
                <Link className="h-4 w-4" />
                Uber Eats
              </a>
            )}
            {deliveryServices?.custom && (
              <a 
                href={formatUrl(deliveryServices.custom)}
                target={deliveryServices.custom.match(/^[\d\s\-\+\(\)]+$/) ? undefined : "_blank"}
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-muted rounded-md text-sm hover:bg-muted/80"
              >
                <Truck className="h-4 w-4" />
                {t.delivery}
              </a>
            )}
          </div>
        </div>
      )}
      
      <div className="mt-6 pt-4 border-t border-border/50 text-center">
        <a 
          href="/pricing"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
        >
          {t.restaurateurCTA}
        </a>
      </div>
    </footer>
  );
};

export default SimpleMenuFooter;
